import { computed, inject, Injectable, signal } from '@angular/core';
import type { Messaging } from 'firebase/messaging';

import { HttpRequestService } from '@core/http';
import { GlobalConfig } from '@shared/configs';
import { PathAPI } from '@shared/constants';
import { HttpRequestMethodType } from '@shared/enums';
import { getStorage, removeStorage, saveStorage } from '@shared/utils/storage.util';
import { ToastService } from './toast.service';

const KEY_PUSH_TOKEN = 'travelpath.pushToken';
const KEY_BANNER_SNOOZE = 'travelpath.pushBannerSnoozeUntil';
const BANNER_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const SW_PATH = '/firebase-messaging-sw.js';

/** What `public/firebase-messaging-sw.js` posts to a visible tab. */
interface PushMessage {
  type: 'travelpath-push';
  title: string;
  body: string;
  /** The notification's `type` (approved, rejected, ...), for its icon. */
  kind?: string;
  url: string;
}

/** A push that arrived while the app was on screen, for CoPushAlert to show. */
export interface ForegroundPush {
  /** Distinct per arrival, so a repeat of the same text still restarts the banner. */
  id: number;
  title: string;
  body: string;
  kind: string;
}

/**
 * iOS delivers web push only to a Home Screen PWA; a Safari tab has no
 * `Notification` at all. iPadOS reports itself as a Mac, hence the touch check.
 */
const isIosBrowserTab = (): boolean => {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return false;
  }
  const ios =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return ios && !standalone;
};

/**
 * Web push through Firebase Cloud Messaging.
 *
 * Injects HttpRequestService rather than ApiCallService: AuthService calls
 * `disable()` on logout, and ApiCallService injects AuthService, so going
 * through it would be a DI cycle. Every call here is silent anyway -- push is
 * a nicety, never something to put a loader or an error toast in front of.
 *
 * The Firebase SDK is imported lazily, so it stays out of the initial bundle
 * and is never fetched at all while the kill switch is off.
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private readonly http = inject(HttpRequestService);
  private readonly toast = inject(ToastService);

  private readonly _supported = signal(false);
  private readonly _permission = signal<NotificationPermission>('default');
  private readonly _snoozed = signal(true);

  private readonly _foreground = signal<ForegroundPush | null>(null);
  private foregroundSeq = 0;

  private messaging: Messaging | null = null;
  private initialized: Promise<void> | null = null;

  readonly supported = this._supported.asReadonly();
  readonly permission = this._permission.asReadonly();
  /** The latest foreground push; a newer one replaces it rather than queueing. */
  readonly foreground = this._foreground.asReadonly();
  readonly needsInstall = signal(GlobalConfig.enablePushNotifications && isIosBrowserTab());

  /** Nothing once the user has decided either way; `denied` is theirs to undo. */
  readonly showEnableBanner = computed(
    () => this._supported() && this._permission() === 'default' && !this._snoozed(),
  );
  readonly showInstallHint = computed(() => this.needsInstall() && !this._snoozed());

  /** Idempotent; every public method awaits it, so call order never matters. */
  init(): Promise<void> {
    this.initialized ??= this.detect();
    return this.initialized;
  }

  private async detect(): Promise<void> {
    if (!GlobalConfig.enablePushNotifications) {
      return;
    }

    const until = Number((await getStorage(KEY_BANNER_SNOOZE)) ?? 0);
    this._snoozed.set(Date.now() < until);

    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      return;
    }

    try {
      const { isSupported } = await import('firebase/messaging');
      if (!(await isSupported())) {
        return;
      }
    } catch {
      return;
    }

    this._permission.set(Notification.permission);
    this._supported.set(true);
    navigator.serviceWorker.addEventListener('message', (event) =>
      this.onForegroundMessage(event.data),
    );
  }

  /**
   * Must run from a tap: iOS refuses the permission prompt otherwise, and
   * Chrome quietly blocks one that arrives unprompted.
   */
  async enable(): Promise<boolean> {
    await this.init();
    if (!this._supported()) {
      return false;
    }

    const permission = await Notification.requestPermission();
    this._permission.set(permission);
    if (permission !== 'granted') {
      return false;
    }

    const ok = await this.sync();
    if (!ok) {
      this.toast.warning('Notifikasi belum bisa diaktifkan. Coba lagi nanti.');
    }
    return ok;
  }

  /**
   * Registers the current token with the API if it is not the one already
   * registered. Runs on every start and login, so a token FCM rotated, or one
   * whose registration failed last time, is picked up without the banner.
   */
  async sync(): Promise<boolean> {
    await this.init();
    if (!this._supported() || this._permission() !== 'granted') {
      return false;
    }

    try {
      const token = await this.fetchToken();
      if (!token) {
        return false;
      }
      if (token === (await getStorage(KEY_PUSH_TOKEN))) {
        return true;
      }

      const { status } = await this.http.request({
        url: PathAPI.notification.device,
        method: HttpRequestMethodType.POST,
        data: { device_id: token },
      });
      /* Not stored on failure, so the next sync tries again. */
      if (status !== 200) {
        return false;
      }

      await saveStorage(KEY_PUSH_TOKEN, token);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * On logout. Best effort throughout: it must never hold up or break the
   * logout, and a 401-triggered logout cannot authenticate the DELETE anyway.
   * The API drops tokens FCM reports as unregistered, which covers that case.
   */
  async disable(): Promise<void> {
    const token = await getStorage(KEY_PUSH_TOKEN);
    if (!token) {
      return;
    }

    await Promise.allSettled([
      this.http.request({
        url: PathAPI.notification.device,
        method: HttpRequestMethodType.DELETE,
        data: { device_id: token },
      }),
      /* A fresh token for the next user on this device. */
      this.messaging
        ? import('firebase/messaging').then(({ deleteToken }) => deleteToken(this.messaging!))
        : Promise.resolve(),
    ]);
    await removeStorage(KEY_PUSH_TOKEN);
  }

  /** The banner's ×: ask again in a week rather than never. */
  async snooze(): Promise<void> {
    this._snoozed.set(true);
    await saveStorage(KEY_BANNER_SNOOZE, String(Date.now() + BANNER_SNOOZE_MS));
  }

  private async fetchToken(): Promise<string | null> {
    const [{ initializeApp, getApps }, { getMessaging, getToken }] = await Promise.all([
      import('firebase/app'),
      import('firebase/messaging'),
    ]);

    const app = getApps()[0] ?? initializeApp(GlobalConfig.FIREBASE_CONFIG);
    this.messaging ??= getMessaging(app);

    await navigator.serviceWorker.register(SW_PATH, { scope: '/' });
    const registration = await navigator.serviceWorker.ready;

    const token = await getToken(this.messaging, {
      vapidKey: GlobalConfig.FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    return token || null;
  }

  dismissForeground(): void {
    this._foreground.set(null);
  }

  private onForegroundMessage(data: unknown): void {
    const message = data as PushMessage | null;
    if (message?.type !== 'travelpath-push') {
      return;
    }
    this._foreground.set({
      id: ++this.foregroundSeq,
      title: message.title,
      body: message.body,
      kind: message.kind ?? '',
    });
  }
}
